// pages/components/support/TicketDetails.jsx
import React from 'react';
import { ArrowLeft, Calendar, User, Mail, MessageSquare, Tag, Clock } from 'lucide-react';

const TicketDetails = ({ ticket, onBack }) => {
if (!ticket) return null;

const getStatusConfig = (status) => {
const configs = {
open: { bg: 'bg-green-100', text: 'text-green-700', label: 'Open' },
'in-progress': { bg: 'bg-blue-100', text: 'text-blue-700', label: 'In Progress' },
resolved: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Resolved' },
closed: { bg: 'bg-slate-100', text: 'text-slate-700', label: 'Closed' }
};
return configs[status] || configs.open;
};

const getPriorityConfig = (priority) => {
const configs = {
low: { bg: 'bg-slate-100', text: 'text-slate-700' },
medium: { bg: 'bg-blue-100', text: 'text-blue-700' },
high: { bg: 'bg-amber-100', text: 'text-amber-700' },
urgent: { bg: 'bg-red-100', text: 'text-red-700' }
};
return configs[priority] || configs.medium;
};

const statusConfig = getStatusConfig(ticket.status);
const priorityConfig = getPriorityConfig(ticket.priority);

const formatDate = (date) => {
return new Date(date).toLocaleDateString('en-US', {
weekday: 'long',
year: 'numeric',
month: 'long',
day: 'numeric',
hour: '2-digit',
minute: '2-digit'
});
};

return (
<div className="max-w-4xl mx-auto">
{/* Back Button */}
<button
onClick={onBack}
className="flex items-center gap-2 text-slate-600 hover:text-slate-900 font-medium mb-6 transition-colors"
>
<ArrowLeft size={20} />
Back to My Tickets
</button>

{/* Ticket Header Card */}
<div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl border-2 border-blue-200 p-6 md:p-8 mb-6 shadow-lg">
<div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
<div className="flex-1">
<div className="flex items-center gap-3 mb-3">
<span className={`px-3 py-1 ${statusConfig.bg} ${statusConfig.text} rounded-full text-sm font-bold`}>
{statusConfig.label}
</span>
<span className={`px-3 py-1 ${priorityConfig.bg} ${priorityConfig.text} rounded-full text-sm font-bold`}>
{ticket.priority.charAt(0).toUpperCase() + ticket.priority.slice(1)} Priority
</span>
</div>
<h1 className="text-3xl font-bold text-slate-900 mb-2">
{ticket.subject}
</h1>
<p className="font-mono text-sm text-slate-600 bg-white px-3 py-1.5 rounded inline-block">
Ticket ID: {ticket.ticketId}
</p>
</div>
</div>

{/* Quick Info Grid */}
<div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
<div className="flex items-center gap-3 bg-white p-4 rounded-lg">
<div className="p-2 bg-blue-100 rounded-lg">
<User className="text-blue-600" size={20} />
</div>
<div>
<p className="text-xs text-slate-600">Created By</p>
<p className="font-semibold text-slate-900">{ticket.name}</p>
</div>
</div>

<div className="flex items-center gap-3 bg-white p-4 rounded-lg">
<div className="p-2 bg-green-100 rounded-lg">
<Mail className="text-green-600" size={20} />
</div>
<div>
<p className="text-xs text-slate-600">Email</p>
<p className="font-semibold text-slate-900">{ticket.email}</p>
</div>
</div>

<div className="flex items-center gap-3 bg-white p-4 rounded-lg">
<div className="p-2 bg-purple-100 rounded-lg">
<Calendar className="text-purple-600" size={20} />
</div>
<div>
<p className="text-xs text-slate-600">Created</p>
<p className="font-semibold text-slate-900">
{new Date(ticket.createdAt).toLocaleDateString('en-US', {
month: 'short',
day: 'numeric',
year: 'numeric'
})}
</p>
</div>
</div>

<div className="flex items-center gap-3 bg-white p-4 rounded-lg">
<div className="p-2 bg-amber-100 rounded-lg">
<Clock className="text-amber-600" size={20} />
</div>
<div>
<p className="text-xs text-slate-600">Last Updated</p>
<p className="font-semibold text-slate-900">
{new Date(ticket.updatedAt).toLocaleDateString('en-US', {
month: 'short',
day: 'numeric',
year: 'numeric'
})}
</p>
</div>
</div>
</div>
</div>

{/* Message Content */}
<div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm mb-6">
<div className="flex items-center gap-2 mb-4">
<MessageSquare className="text-blue-600" size={24} />
<h2 className="text-xl font-bold text-slate-900">Message</h2>
</div>

<div className="prose prose-slate max-w-none">
<p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
{ticket.message}
</p>
</div>
</div>

{/* Timeline */}
<div className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm">
<h2 className="text-xl font-bold text-slate-900 mb-6">Ticket Timeline</h2>

<div className="space-y-6">
<div className="flex gap-4">
<div className="flex flex-col items-center">
<div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
<div className="w-3 h-3 rounded-full bg-green-600"></div>
</div>
<div className="w-0.5 h-full bg-slate-200 mt-2"></div>
</div>
<div className="flex-1 pb-6">
<p className="font-semibold text-slate-900">Ticket Created</p>
<p className="text-sm text-slate-600 mt-1">{formatDate(ticket.createdAt)}</p>
<p className="text-sm text-slate-500 mt-2">
Ticket submitted and awaiting review by support team
</p>
</div>
</div>

{ticket.status !== 'open' && (
<div className="flex gap-4">
<div className="flex flex-col items-center">
<div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
<div className="w-3 h-3 rounded-full bg-blue-600"></div>
</div>
{ticket.status !== 'closed' && <div className="w-0.5 h-full bg-slate-200 mt-2"></div>}
</div>
<div className="flex-1 pb-6">
<p className="font-semibold text-slate-900">Status Updated</p>
<p className="text-sm text-slate-600 mt-1">{formatDate(ticket.updatedAt)}</p>
<p className="text-sm text-slate-500 mt-2">
Ticket status changed to <span className="font-semibold">{ticket.status}</span>
</p>
</div>
</div>
)}
</div>
</div>

{/* Action Buttons */}
{ticket.status === 'open' || ticket.status === 'in-progress' ? (
<div className="mt-6 p-6 bg-blue-50 border border-blue-200 rounded-xl">
<h3 className="font-semibold text-blue-900 mb-2">⏰ In Progress</h3>
<p className="text-sm text-blue-800 mb-4">
Our support team is reviewing your ticket. We typically respond within 24 hours.
</p>
<p className="text-xs text-blue-700">
You'll receive an email notification when there's an update on your ticket.
</p>
</div>
) : null}
</div>
);
};

export default TicketDetails;